
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { CardImage } from "./card/CardImage";
import { CardDescription } from "./card/CardDescription";
import { CardContactItem } from "./card/CardContactItem";
import { CardActions } from "./card/CardActions";
import { CardWaitlist } from "./card/CardWaitlist";
import { NursingHome } from "@/hooks/use-nursing-home";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, Phone } from "lucide-react";

interface NursingHomeCardProps {
  nursingHome: NursingHome & {
    id?: number; // Added from search results
    distance?: number; // Added for location sort
  };
  hideBorder?: boolean;
  size?: "sm" | "md" | "lg";
}

export function NursingHomeCard({
  nursingHome,
  hideBorder = false,
  size = "md",
}: NursingHomeCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isInLeads, setIsInLeads] = useState(false);

  console.log("Rendering NursingHomeCard with data:", { 
    name: nursingHome.center_name,
    slug: nursingHome.nh_slug,
    county: nursingHome.county_slug,
    address: nursingHome.standardized_address
  });

  // Check if nursing home is in user's leads
  React.useEffect(() => {
    const checkLeads = async () => {
      if (user) {
        const { data, error } = await supabase
          .from("nursing_home_leads")
          .select("id")
          .eq("user_id", user.id)
          .eq("nursing_home_id", nursingHome.nh_slug)
          .limit(1);

        if (!error && data) {
          setIsInLeads(data.length > 0);
        }
      }
    };

    checkLeads();
  }, [user, nursingHome.nh_slug]);

  const handleCardClick = () => {
    if (nursingHome.county_slug && nursingHome.nh_slug) {
      navigate(`/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}`);
    }
  };

  const handleTrackClick = (type: string) => {
    // Analytics tracking for click events
    console.log(`Tracking ${type} for ${nursingHome.center_name}`);
    // Here you would implement your actual tracking logic
  };

  // Determine nursing home category
  const category = nursingHome.category || "Nursing Home";

  return (
    <Card
      className={`relative overflow-hidden transition-shadow duration-300 hover:shadow-lg ${
        !hideBorder ? "" : "border-none shadow-none"
      } flex flex-col`}
    >
      <div className="flex-1" onClick={handleCardClick} role="button" tabIndex={0}>
        {/* Card Image */}
        <CardImage
          nhSlug={nursingHome.nh_slug}
          centerName={nursingHome.center_name || ""}
          address={nursingHome.standardized_address}
        />

        <CardHeader className="pb-2">
          <h3 className={`font-semibold truncate ${size === "sm" ? "text-md" : "text-xl"}`} title={nursingHome.center_name || "Unnamed Nursing Home"}>
            {nursingHome.center_name || "Unnamed Nursing Home"}
          </h3>
          <p className="text-gray-500 text-sm">
            {nursingHome.city_town || ""}
          </p>
        </CardHeader>

        <CardContent>
          <CardDescription
            description={nursingHome.short_description || nursingHome.description}
            maxResidents={nursingHome.max_residents}
            category={category}
          />

          <div className="mt-4 space-y-2">
            {nursingHome.standardized_address && (
              <CardContactItem
                icon={MapPin}
                content={nursingHome.standardized_address}
                type="address"
              />
            )}

            {nursingHome.standardized_phone_number && (
              <CardContactItem
                icon={Phone}
                content={nursingHome.standardized_phone_number}
                type="phone"
              />
            )}
          </div>

          <div className="mt-6">
            <CardActions
              centerName={nursingHome.center_name || ""}
              nhSlug={nursingHome.nh_slug}
              countySlug={nursingHome.county_slug || ""}
              websiteUrl={nursingHome.official_website}
              onTrackClick={handleTrackClick}
              isInLeads={isInLeads}
              setIsInLeads={setIsInLeads}
              email={nursingHome.email}
            />
          </div>
        </CardContent>
      </div>
    </Card>
  );
}
